import { ElementRef, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { FormViewerInterface } from '../interface/form-viewer.interface';
import { FormBuilderUDFService } from './form-builder-udf-service';
import { BaseApi } from 'src/backend/api/base-api';

@Injectable({
  providedIn: 'root'
})
export class SharedOperationService {

  private formViewerSource = new BehaviorSubject<FormViewerInterface>({
    json_data: null,
    html_data: null,
    container: null,
    renderer: null,
  });
  formViewerData$ = this.formViewerSource.asObservable();
  dataFromShared: any;

  setFormViewerData(data: FormViewerInterface) {
    this.formViewerSource.next(data);
  }
  constructor(private formService: FormBuilderUDFService, private baseApi: BaseApi) {
    this.formViewerData$.subscribe((data) => {
      if (data) {
        this.dataFromShared = data;
      }
    });
  }

  async fetchDataFromAPI(params: any, url: string, requestType: string, apiDirectoryId?: number, isExternalAPI?: boolean): Promise<any> {
    if (apiDirectoryId && isExternalAPI !== false) {
      try {
        return await this.formService.callExternalAPI(apiDirectoryId, params).toPromise();
      } catch (error) {
        console.error('Error calling external API proxy', error);
        throw error;
      }
    }
    try {
      const observable = this.formService.getDataFromAPI(params, url, requestType);
      if (!observable) {
        throw new Error('getDataFromAPI returned undefined');
      }
      const result = await observable.toPromise();
      return result;
    } catch (error) {
      console.error('Error fetching data from API', error);
      throw error;
    }
  }

  constructDependentParams(dependentDropdownName: string, container: HTMLElement, getDropdownConfig: Function, parentDropdownName?: string): any {
    const paramData: { [key: string]: any } = {};
    const elementConfig = getDropdownConfig(dependentDropdownName);
    if (elementConfig?.element?.datasourceParamaters) {
      const params = JSON?.parse(elementConfig?.element?.datasourceParamaters);
      // Resolve a configured param value: if it names a real form field, use that field's value.
      // Otherwise (e.g. a predefined placeholder like "0") fall back to the value of the parent
      // dropdown that triggered this update, so the dependency cascades (group → plant → line → unit).
      const resolveValue = (value: string) => {
        if (this.fieldExists(value, container)) {
          return this.getSelectedValue(value, container);
        }
        if (parentDropdownName && this.fieldExists(parentDropdownName, container)) {
          return this.getSelectedValue(parentDropdownName, container);
        }
        return this.getSelectedValue(value, container);
      };
      if (Array.isArray(params)) {
        params.forEach((obj: any) => {
          Object.entries(obj).forEach(([key, value]) => {
            paramData[key] = `${resolveValue(value as string)}`;
          });
        });
      } else if (typeof params === 'object' && params !== null) {
        Object.entries(params).forEach(([key, value]) => {
          paramData[key] = `${resolveValue(value as string)}`;
        });
      } else {
        console.error('Invalid datasource parameters structure:', params);
      }
    }
    return paramData;
  }

  /** True when a form field (select/input/textarea/etc.) with the given name exists in the container. */
  private fieldExists(fieldName: string, container: HTMLElement): boolean {
    if (!fieldName) return false;
    const selectors = [
      `select[name="${fieldName}"]`,
      `input[name="${fieldName}"]`,
      `textarea[name="${fieldName}"]`,
      `button[name="${fieldName}"]`,
      `datalist[name="${fieldName}"]`,
      `output[name="${fieldName}"]`
    ];
    for (const selector of selectors) {
      const field = container === undefined
        ? this.dataFromShared?.container?.nativeElement?.querySelector(selector)
        : container?.querySelector(selector);
      if (field) return true;
    }
    return false;
  }

  getSelectedValue(fieldName: string, container: HTMLElement): string | any {
    let field: HTMLElement | null = null;

    //here define all posible elements 
    const selectors = [
      `select[name="${fieldName}"]`,
      `input[name="${fieldName}"]`,
      `textarea[name="${fieldName}"]`,
      `button[name="${fieldName}"]`,
      `datalist[name="${fieldName}"]`,
      `output[name="${fieldName}"]`
    ];

    for (const selector of selectors) {
      field = container === undefined ? this.dataFromShared?.container.nativeElement.querySelector(selector) : container.querySelector(selector);
      if (field) break;
    }

    if (field) {
      if (field instanceof HTMLSelectElement) {
        return field.value !== null ? field.value : 0;
      } else if (field instanceof HTMLInputElement) {
        if (field.type === 'checkbox' || field.type === 'radio') {
          return field.checked ? field.value : 0;
        }
        return field.value !== null ? field.value : 0;
      } else if (field instanceof HTMLTextAreaElement) {
        return field.value !== null ? field.value : 0;
      } else if (field instanceof HTMLButtonElement) {
        return field.value !== null ? field.value : 0;
      } else if (field instanceof HTMLDataListElement) {
        return field.innerHTML !== null ? field.innerHTML : 0;
      } else if (field instanceof HTMLOutputElement) {
        return field.value !== null ? field.value : 0;
      }
    }

    // If no field is found, return 0 or a default value razique
    return 0;
  }



  getFormValues(el: ElementRef): any {
    type FormValue = string | { value: any; text: any };
    const formElement = el.nativeElement.querySelector('form');
    const formValues: { [key: string]: FormValue } = {};

    if (formElement) {
      const inputs = formElement.querySelectorAll('input, select, textarea');
      inputs.forEach((input: any) => {
        const name = input?.name;
        let value: FormValue = '';

        if (input.type === 'radio' && input.checked) {
          value = input?.value;
        } else if (input?.type === 'checkbox') {
          value = input?.checked.toString();
        } else if (input?.type === 'date') {
          value = input?.value ? new Date(input?.value).toISOString() : '';
        } else if (input?.tagName.toLowerCase() === 'select') {
          value = {
            value: input?.value,
            text: input?.options[input?.selectedIndex]?.text
          };
        } else if (input?.type !== 'radio') {
          value = input?.value;
        }
        if (name && value !== '') {
          formValues[name] = value;
        }
      });
    }

    return formValues;
  }

  getFormValuesForFilter(el: ElementRef): any {
    type FormValue = string | { value: any; text: any };
    const formElement = el.nativeElement.querySelector('form');
    const formValues: { [key: string]: FormValue } = {};

    if (formElement) {
      const inputs = formElement.querySelectorAll('input, select, textarea');
      inputs.forEach((input: any) => {
        const name = input.name;
        let value: FormValue = '';

        if (input.type === 'radio' && input.checked) {
          value = input.value;
        } else if (input.type === 'checkbox') {
          value = input.checked.toString();
        } else if (input.tagName.toLowerCase() === 'select') {
          value = input.value;

        } else if (input.type !== 'radio') {
          value = input.value;
        }

        if (name && value !== '') {
          formValues[name] = value;
        }
      });
    }

    return formValues;
  }

  async getTableName(formId: any): Promise<string> {
    return new Promise((resolve, reject) => {
      this.formService.getTableName(formId).subscribe({
        next: (response: { Table: { tableName: string | undefined; }[]; }) => {
          const tableName = response.Table[0]?.tableName || '';
          console.log('Table Name:', tableName);
          resolve(tableName);
        },
        error: (err) => {
          console.error('Error fetching table name:', err);
          reject(err);
        }
      });
    });
  }

  initializePlugins() {
    if (typeof $ !== 'undefined') {
      $(document).ready(() => {
        // Initialize datetime pickers
        $('div[id^="datetime_input_"]').each(function () {
          $(this).datetimepicker({
            format: "YYYY-MM-DD HH:mm:ss",
            focusOnShow: false,
            defaultDate: new Date(), // Set current datetime as default value
            icons: {
              time: 'far fa-clock',
              date: 'far fa-calendar',
              up: 'fas fa-arrow-up',
              down: 'fas fa-arrow-down',
              previous: 'fas fa-chevron-left',
              next: 'fas fa-chevron-right',
              today: 'far fa-calendar-check-o',
              clear: 'far fa-trash',
              close: 'far fa-times'
            }
          });
        });

        // Initialize date pickers
        $('div[id^="date_input_"]').each(function () {
          $(this).datetimepicker({
            format: "YYYY-MM-DD",
            focusOnShow: false,
            defaultDate: new Date(), // Set current date as default value
            icons: {
              time: 'far fa-clock',
              date: 'far fa-calendar',
              up: 'fas fa-arrow-up',
              down: 'fas fa-arrow-down',
              previous: 'fas fa-chevron-left',
              next: 'fas fa-chevron-right',
              today: 'far fa-calendar-check-o',
              clear: 'far fa-trash',
              close: 'far fa-times'
            }
          });
        });

        // Initialize other plugins if necessary
      });
    } else {
      console.error('jQuery is not loaded');
    }
  }
}
